import React, { useState, useMemo } from 'react';
import { Rule, MarketSnapshot, Trade } from '../types';
import { MarketDataState, SUPPORTED_SYMBOLS } from '../hooks/useMarketData';
import { getActiveRules } from '../utils/ruleEngine';
import TradingViewWidget from './TradingViewWidget';
import CryptoHeatmap from './CryptoHeatmap';
import EconomicCalendar from './EconomicCalendar';

interface MarketDashboardProps {
  marketData: MarketDataState;
  rules: Rule[];
  onGoToJournal: (prefill: Partial<Trade>) => void;
}

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtOI(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${fmt(n, 0)}`;
}


function getFundingLevel(f: number): { label: string; color: string; bg: string; desc: string } {
  if (f >= 0.05)  return { label: '⚠️ 위험',      color: 'text-red-700',    bg: 'bg-red-100 border-red-200',       desc: '과도한 롱 쏠림 — 급락 위험' };
  if (f >= 0.03)  return { label: '🔥 과열',      color: 'text-orange-600', bg: 'bg-orange-100 border-orange-200', desc: '롱 과열 — 숏 시그널 구간' };
  if (f >= 0.01)  return { label: '📈 롱 많음',   color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200',  desc: '롱 포지션 우세' };
  if (f <= -0.05) return { label: '🚀 스퀴즈 가능', color: 'text-emerald-700', bg: 'bg-emerald-100 border-emerald-200', desc: '숏 과집중 — 숏 스퀴즈 가능성' };
  if (f <= -0.03) return { label: '😱 과공포',    color: 'text-blue-700',   bg: 'bg-blue-100 border-blue-200',    desc: '극단적 숏 쏠림 — 반등 가능' };
  if (f <= -0.01) return { label: '📉 숏 많음',   color: 'text-blue-500',   bg: 'bg-blue-50 border-blue-200',     desc: '숏 포지션 우세' };
  return           { label: '➖ 중립',           color: 'text-gray-500',   bg: 'bg-gray-50 border-gray-200',     desc: '뚜렷한 쏠림 없음' };
}

const FUNDING_GUIDE = [
  { range: '+0.05% 이상', label: '⚠️ 위험',        desc: '과도한 롱 쏠림 — 급락 위험',      color: 'text-red-600' },
  { range: '+0.03% 이상', label: '🔥 과열',         desc: '롱 과열 — 숏 시그널 구간',         color: 'text-orange-500' },
  { range: '+0.01% 이상', label: '📈 롱 많음',      desc: '롱 포지션 우세',                   color: 'text-yellow-600' },
  { range: '-0.01% 이하', label: '📉 숏 많음',      desc: '숏 포지션 우세',                   color: 'text-blue-500' },
  { range: '-0.03% 이하', label: '😱 과공포',       desc: '극단적 숏 쏠림 — 반등 가능',       color: 'text-blue-600' },
  { range: '-0.05% 이하', label: '🚀 스퀴즈 가능',  desc: '숏 과집중 — 숏 스퀴즈 가능성',     color: 'text-emerald-600' },
];

// ── 교육 가이드 데이터 ────────────────────────────────────────────────────────
interface ConceptGuide {
  id: string;
  emoji: string;
  title: string;
  color: string;
  what: string;
  how: string;
  signal: { icon: string; text: string }[];
  tip: string;
  combos?: { funding: string; oi: string; price: string; label: string; color: string }[];
  examples?: { title: string; detail: string; conclusion: string }[];
}

const CONCEPT_GUIDES: ConceptGuide[] = [
  {
    id: 'funding',
    emoji: '💸',
    title: '펀딩비 (Funding Rate)',
    color: 'orange',
    what: '선물 거래에서 롱·숏 포지션 간 균형을 맞추기 위해 8시간마다 주고받는 수수료입니다.',
    how: '롱이 많으면 펀딩비가 양수(+) → 롱 보유자가 숏 보유자에게 돈을 냄. 숏이 많으면 음수(-) → 반대 방향.',
    signal: [
      { icon: '🔥', text: '펀딩비가 높을수록 롱 과열 → 가격 조정 가능성 ↑' },
      { icon: '📉', text: '펀딩비가 낮을수록 공포 → 반등 가능성 ↑' },
    ],
    tip: '펀딩비 0.01%는 하루 약 0.03% 비용. 0.1% 이상이면 포지션 유지 비용이 매우 높아짐.',
  },
  {
    id: 'oi',
    emoji: '📊',
    title: 'OI — 미결제약정 (Open Interest)',
    color: 'blue',
    what: '현재 시장에 열려 있는 선물 포지션의 총 규모(달러)입니다. 아직 닫히지 않은 계약 합계.',
    how: '새 포지션이 생기면 OI 증가 → 돈이 시장에 들어오는 것. 청산·종료되면 OI 감소 → 포지션이 닫히는 것.',
    signal: [
      { icon: '① 가격 ↑ OI ↑', text: '→ 진짜 상승. 새 매수 포지션 진입 중. 추세 신뢰 가능.' },
      { icon: '② 가격 ↓ OI ↑', text: '→ 진짜 하락. 새 매도 포지션 진입 중. 추세 신뢰 가능.' },
      { icon: '③ 가격 ↑ OI ↓', text: '→ 숏 청산. 숏이 터지며 가격 올라간 것. 진짜 상승 아닐 수 있음.' },
      { icon: '④ 가격 ↓ OI ↓', text: '→ 롱 청산. 롱이 터지며 가격 내려간 것. 진짜 하락 아닐 수 있음.' },
    ],
    tip: '차트만 보면 속는다. 가격이 올라도 OI가 줄면 숏 청산일 수 있고, OI가 늘면 진짜 매수 진입이다. OI는 고수 필수 지표.',
    combos: [
      { funding: '펀딩 +', oi: 'OI ↑', price: '가격 ↑', label: '롱 과열', color: 'text-red-600 bg-red-50' },
      { funding: '펀딩 -', oi: 'OI ↑', price: '가격 ↓', label: '숏 과열', color: 'text-blue-600 bg-blue-50' },
      { funding: '펀딩 +', oi: 'OI ↓', price: '가격 ↑', label: '숏 청산 (스퀴즈)', color: 'text-orange-600 bg-orange-50' },
      { funding: '펀딩 -', oi: 'OI ↓', price: '가격 ↓', label: '롱 청산 (하락)', color: 'text-purple-600 bg-purple-50' },
    ],
    examples: [
      {
        title: 'BTC 상승 — 진짜 상승',
        detail: '펀딩 + / OI ↑ / 가격 ↑',
        conclusion: '새 롱 포지션 진입 중. 롱 쏠림이 심해지면 이후 조정 주의.',
      },
      {
        title: 'BTC 급등 — 숏 스퀴즈',
        detail: '펀딩 + / OI ↓ / 가격 ↑',
        conclusion: '숏이 터지며 가격 올라간 것. 진짜 매수 아닐 수 있음. 급등 후 재하락 가능.',
      },
      {
        title: 'BTC 하락 — 진짜 하락',
        detail: '펀딩 - / OI ↑ / 가격 ↓',
        conclusion: '새 숏 포지션 진입 중. 하락 추세 신뢰 가능. 반등 롱 섣불리 잡지 말 것.',
      },
    ],
  },
  {
    id: 'liq',
    emoji: '💥',
    title: '청산 (Liquidation)',
    color: 'red',
    what: '레버리지 포지션이 손실로 증거금이 부족해지면 거래소가 강제로 포지션을 닫는 것입니다.',
    how: '롱 청산 → 가격이 더 내려가는 방향으로 작용. 숏 청산 → 가격이 더 올라가는 방향으로 작용.',
    signal: [
      { icon: '🔴', text: '롱 청산 급증 → 매도 압력 증가 → 추가 하락 가능' },
      { icon: '🟢', text: '숏 청산 급증 → 숏 스퀴즈 → 급등 가능 (롱 기회)' },
      { icon: '💰', text: '청산 후엔 반대 방향 포지션 기회를 노릴 수 있음' },
    ],
    tip: '레버리지 10배 = 가격 10% 역방향 이동 시 100% 손실(청산). 레버리지가 높을수록 청산존이 가깝다.',
  },
  {
    id: 'longshortratio',
    emoji: '⚖️',
    title: '롱·숏 비율',
    color: 'purple',
    what: '현재 시장 참여자 중 롱 포지션과 숏 포지션의 비율입니다.',
    how: '거래소마다 계산 방식이 다르지만, 롱 비율이 높으면 시장이 상승을 기대하는 분위기.',
    signal: [
      { icon: '📈', text: '롱 70% 이상 → 롱 쏠림 → 역방향(하락) 가능성 주의' },
      { icon: '📉', text: '숏 70% 이상 → 숏 쏠림 → 숏 스퀴즈(급등) 주의' },
      { icon: '⚖️', text: '50:50 근처 → 방향성 불명확 → 추세 돌파 대기' },
    ],
    tip: '대중이 많이 몰린 방향이 틀리는 경우가 많다. 극단적 쏠림은 반대 방향의 신호로 활용.',
  },
];

const COLOR_MAP: Record<string, { border: string; bg: string; badge: string; title: string }> = {
  orange: { border: 'border-orange-200', bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700', title: 'text-orange-700' },
  blue:   { border: 'border-blue-200',   bg: 'bg-blue-50',   badge: 'bg-blue-100 text-blue-700',     title: 'text-blue-700' },
  red:    { border: 'border-red-200',    bg: 'bg-red-50',    badge: 'bg-red-100 text-red-700',       title: 'text-red-700' },
  purple: { border: 'border-purple-200', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', title: 'text-purple-700' },
};

const ConceptCard: React.FC<{ guide: ConceptGuide }> = ({ guide }) => {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[guide.color];
  return (
    <div className={`rounded-2xl border ${c.border} bg-white/70 overflow-hidden`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/80 transition-colors"
      >
        <span className="text-2xl">{guide.emoji}</span>
        <div className="flex-1">
          <span className={`font-bold text-sm ${c.title}`}>{guide.title}</span>
          {!open && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{guide.what}</p>}
        </div>
        <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={`px-5 pb-5 space-y-4 ${c.bg} border-t ${c.border}`}>
          <div className="pt-4">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">📌 무엇인가</p>
            <p className="text-sm text-gray-700 leading-relaxed">{guide.what}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">⚙️ 어떻게 작동하나</p>
            <p className="text-sm text-gray-700 leading-relaxed">{guide.how}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">🎯 시그널 해석</p>
            <div className="space-y-1.5">
              {guide.signal.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-base leading-none mt-0.5">{s.icon}</span>
                  <p className="text-sm text-gray-700">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
          {guide.combos && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">🔗 펀딩 + OI 조합 시그널</p>
              <div className="space-y-1.5">
                {guide.combos.map((c2, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold ${c2.color}`}>
                    <span className="font-mono shrink-0">{c2.funding}</span>
                    <span className="text-gray-400">/</span>
                    <span className="font-mono shrink-0">{c2.oi}</span>
                    <span className="text-gray-400">/</span>
                    <span className="font-mono shrink-0">{c2.price}</span>
                    <span className="ml-auto font-bold">→ {c2.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {guide.examples && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">📖 실제 예시</p>
              <div className="space-y-2">
                {guide.examples.map((ex, i) => (
                  <div key={i} className="bg-white/70 rounded-xl px-3 py-2.5 border border-gray-100">
                    <p className="text-xs font-bold text-gray-700">{ex.title}</p>
                    <p className="text-xs font-mono text-gray-500 mt-0.5">{ex.detail}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">→ {ex.conclusion}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={`rounded-xl px-4 py-3 ${c.badge.replace('text-', 'border-').replace(/\w+-\d+/, '')} bg-white/60 border ${c.border}`}>
            <p className="text-xs font-bold mb-0.5">💡 TIP</p>
            <p className="text-xs text-gray-600 leading-relaxed">{guide.tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const TrendBadge: React.FC<{ trend: 'up' | 'down' | 'neutral' }> = ({ trend }) => {
  const cls = trend === 'up' ? 'text-green-600 bg-green-50' : trend === 'down' ? 'text-red-500 bg-red-50' : 'text-gray-400 bg-gray-50';
  const label = trend === 'up' ? '▲ 상승' : trend === 'down' ? '▼ 하락' : '— 중립';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cls}`}>{label}</span>;
};

const SnapshotTable: React.FC<{ snapshot: MarketSnapshot }> = ({ snapshot }) => {
  const fundingLevel = getFundingLevel(snapshot.funding);
  return (
  <div className="space-y-3">
    {/* 펀딩비 상태 배지 */}
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${fundingLevel.bg}`}>
      <span className={`text-base font-extrabold ${fundingLevel.color}`}>{fundingLevel.label}</span>
      <span className={`font-mono font-bold text-sm ${fundingLevel.color}`}>
        {snapshot.funding >= 0 ? '+' : ''}{fmt(snapshot.funding, 4)}%
      </span>
      <span className={`text-xs ${fundingLevel.color} opacity-80`}>— {fundingLevel.desc}</span>
    </div>

    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs text-gray-500 border-b border-gray-100">
          <th className="text-left py-2 px-3 font-semibold">항목</th>
          <th className="text-right py-2 px-3 font-semibold">값</th>
          <th className="text-right py-2 px-3 font-semibold">추세</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        <tr>
          <td className="py-2 px-3 text-gray-600">가격</td>
          <td className="py-2 px-3 text-right font-mono font-bold text-gray-800">${fmt(snapshot.price, 0)}</td>
          <td className="py-2 px-3 text-right">
            <span className={snapshot.priceChange24h >= 0 ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>
              {snapshot.priceChange24h >= 0 ? '+' : ''}{fmt(snapshot.priceChange24h)}%
            </span>
          </td>
        </tr>
        <tr>
          <td className="py-2 px-3 text-gray-600">펀딩비</td>
          <td className={`py-2 px-3 text-right font-mono font-bold ${snapshot.funding > 0 ? 'text-orange-500' : snapshot.funding < 0 ? 'text-blue-500' : 'text-gray-600'}`}>
            {snapshot.funding >= 0 ? '+' : ''}{fmt(snapshot.funding, 4)}%
          </td>
          <td className="py-2 px-3 text-right">
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${fundingLevel.bg} ${fundingLevel.color}`}>
              {fundingLevel.label}
            </span>
          </td>
        </tr>
        <tr>
          <td className="py-2 px-3 text-gray-600">OI</td>
          <td className="py-2 px-3 text-right font-mono font-bold text-gray-800">{fmtOI(snapshot.oi)}</td>
          <td className="py-2 px-3 text-right"><TrendBadge trend={snapshot.oiTrend} /></td>
        </tr>
        <tr>
          <td className="py-2 px-3 text-gray-600">Liquidation</td>
          <td className="py-2 px-3 text-right text-gray-500 text-xs">추정</td>
          <td className="py-2 px-3 text-right"><TrendBadge trend={snapshot.liqTrend} /></td>
        </tr>
        <tr>
          <td className="py-2 px-3 text-gray-600">롱 비율</td>
          <td className="py-2 px-3 text-right font-mono text-green-600 font-bold">{fmt(snapshot.longRatio, 1)}%</td>
          <td className="py-2 px-3 text-right font-mono text-red-500 font-bold">숏 {fmt(snapshot.shortRatio, 1)}%</td>
        </tr>
      </tbody>
    </table>
    </div>
  </div>
  );
};

const MarketDashboard: React.FC<MarketDashboardProps> = ({ marketData, rules, onGoToJournal }) => {
  const [selectedCoin, setSelectedCoin] = useState<string>('BTC');
  const { snapshots, isLoading, error, lastUpdated, refetch } = marketData;

  const snapshot = snapshots[selectedCoin] ?? null;

  const activeRules = useMemo(() => {
    if (!snapshot) return [];
    return getActiveRules(rules, snapshot);
  }, [rules, snapshot]);

  const handleRecordSignal = () => {
    if (!snapshot) return;
    const prefill: Partial<Trade> = {
      date: new Date().toISOString().slice(0, 10),
      symbol: selectedCoin,
      funding: snapshot.funding,
      oi: snapshot.oi,
      oiTrend: snapshot.oiTrend,
      liqTrend: snapshot.liqTrend,
      ruleIds: activeRules.map(r => r.id),
    };
    onGoToJournal(prefill);
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Market Dashboard</h2>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              갱신: {new Date(lastUpdated).toLocaleTimeString('ko-KR')}
            </p>
          )}
        </div>
        <button
          onClick={refetch}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {isLoading ? '로딩...' : '↻ 새로고침'}
        </button>
      </div>

      {/* 코인 탭 */}
      <div className="flex gap-2 flex-wrap">
        {SUPPORTED_SYMBOLS.map(coin => (
          <button
            key={coin}
            onClick={() => setSelectedCoin(coin)}
            className={`px-5 py-2 rounded-xl font-bold text-sm transition-all ${selectedCoin === coin
              ? 'bg-fuchsia-500 text-white shadow-md'
              : 'bg-white/60 text-gray-500 hover:bg-white border border-gray-200'
            }`}
          >
            {coin}
          </button>
        ))}
      </div>

      {/* 펀딩비 안내표 */}
      <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3">📋 펀딩비 해석 가이드</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FUNDING_GUIDE.map(item => (
            <div key={item.range} className="flex flex-col gap-0.5 px-3 py-2 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-extrabold ${item.color}`}>{item.label}</span>
              </div>
              <span className="text-[11px] text-gray-400 font-mono">{item.range}</span>
              <span className="text-[11px] text-gray-500">{item.desc}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* 시장 데이터 테이블 */}
        <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">{selectedCoin} 시장 현황</h3>
          {error && (
            <div className="text-red-500 text-sm bg-red-50 rounded-lg p-3 mb-3">{error}</div>
          )}
          {isLoading && !snapshot ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : snapshot ? (
            <SnapshotTable snapshot={snapshot} />
          ) : (
            <p className="text-gray-400 text-sm">데이터를 불러오는 중...</p>
          )}
        </div>

        {/* 룰 시그널 패널 */}
        <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">활성 룰 시그널</h3>
          {activeRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <span className="text-3xl mb-2">—</span>
              <p className="text-sm">현재 발동된 룰이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeRules.map(rule => (
                <div
                  key={rule.id}
                  className={`flex items-center justify-between px-4 py-2.5 rounded-xl font-semibold text-sm ${rule.signalType === 'long'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                  }`}
                >
                  <span>{rule.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${rule.signalType === 'long' ? 'bg-green-100' : 'bg-red-100'}`}>
                    {rule.signalType === 'long' ? '🟢 LONG' : '🔴 SHORT'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {snapshot && (
            <button
              onClick={handleRecordSignal}
              className="mt-4 w-full py-2 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-bold transition-colors"
            >
              📝 현재 상태로 거래 기록하기
            </button>
          )}
        </div>
      </div>

      {/* 시장 히트맵 */}
      <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3">🗺️ 시장 히트맵</h3>
        <CryptoHeatmap />
      </div>

      {/* TradingView 차트 */}
      <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3">{selectedCoin}/USDT 차트</h3>
        <TradingViewWidget key={selectedCoin} symbol={selectedCoin} />
      </div>

      {/* 경제 캘린더 */}
      <div className="bg-white/60 backdrop-blur border border-white/30 rounded-2xl shadow p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3">📅 경제 캘린더 <span className="text-xs font-normal text-gray-400 ml-1">FOMC · CPI · 주요 매크로 이벤트</span></h3>
        <EconomicCalendar />
      </div>

      {/* 개념 학습 가이드 */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-700">📚 지표 학습 가이드 <span className="text-xs font-normal text-gray-400 ml-1">— 클릭해서 펼치기</span></h3>
        {CONCEPT_GUIDES.map(guide => (
          <ConceptCard key={guide.id} guide={guide} />
        ))}
      </div>

    </div>
  );
};

export default MarketDashboard;
